import { Translation } from '@ps/web-shared-components';
import classnames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type {
  BetReceipt,
  MultipleReceiptItem,
  SingleReceiptItem
} from '@swf/core/models/sports/betSlip/types';
import { select } from '@swf/core/store';

import {
  TXTWCL_Bonus_Cash,
  TXTWCL_Free_Bet,
  TXTWCL_Total_Potential_Returns,
  TXTWCL_Total_Stake,
  TXTWCL_n_a
} from './Totals.messages.json';
import styles from './Totals.scss';

type MergedReceiptItems = (SingleReceiptItem | MultipleReceiptItem)[];

const mergeReceiptItems = (receipts: BetReceipt): MergedReceiptItems => [
  ...receipts.SINGLE,
  ...receipts.MULTIPLE
];

const getStartingPriceReceiptItem = (receiptItems: MergedReceiptItems) =>
  receiptItems.find((receiptItem) => receiptItem.totalPotentialWin === 0);
const getBonusCashReceiptItem = (receiptItems: MergedReceiptItems) =>
  receiptItems.find((receiptItem) => receiptItem.appliedBonusCash && !receiptItem.isFreeBet);
const getFreeBetsReceiptItem = (receiptItems: MergedReceiptItems) =>
  receiptItems.find((receiptItem) => receiptItem.isFreeBet);

const calculateTotals = (receiptItems: MergedReceiptItems) =>
  receiptItems.reduce(
    (totals, receiptItem) => {
      const receiptItemStake = receiptItem.totalStake || 0;
      const receiptItemPotentialReturn =
        (receiptItem.totalPotentialWin || 0) -
        (receiptItem.isFreeBet ? receiptItem.totalStake || 0 : 0);

      totals.totalStake = totals.totalStake + receiptItemStake;
      totals.totalPotentialReturns = totals.totalPotentialReturns + receiptItemPotentialReturn;

      return totals;
    },
    {
      totalStake: 0,
      totalPotentialReturns: 0
    }
  );

const Totals: React.FC = () => {
  const receipt = useSelector(select.betSlip.receipt);
  const mergedReceiptItems = mergeReceiptItems(receipt);

  const isStartingPriceReceiptItem = !!getStartingPriceReceiptItem(mergedReceiptItems);
  const bonusCashReceiptItem = getBonusCashReceiptItem(mergedReceiptItems);
  const freeBetsReceiptItem = getFreeBetsReceiptItem(mergedReceiptItems);

  const { totalStake, totalPotentialReturns } = calculateTotals(mergedReceiptItems);

  return (
    <div className={styles.wrapper}>
      <ul className={styles.returns}>
        <li>
          <span>
            <Translation {...TXTWCL_Total_Stake} />
          </span>

          <span className={styles.value}>
            <CurrencyAmount value={totalStake} />
          </span>
        </li>

        {!!bonusCashReceiptItem && (
          <li>
            <span>
              <Translation {...TXTWCL_Bonus_Cash} />
            </span>

            <span className={classnames(styles.value, styles.bonusCash)}>
              <CurrencyAmount value={bonusCashReceiptItem.appliedBonusCash || 0} />
            </span>
          </li>
        )}

        {!!freeBetsReceiptItem && (
          <li>
            <span>
              <Translation {...TXTWCL_Free_Bet} />
            </span>

            <span className={classnames(styles.value, styles.bonusCash)}>
              <CurrencyAmount value={freeBetsReceiptItem.appliedBonusCash || 0} />
            </span>
          </li>
        )}

        <li>
          <span>
            <Translation {...TXTWCL_Total_Potential_Returns} />
          </span>

          <span className={classnames(styles.value, styles.returnValue)}>
            {isStartingPriceReceiptItem ? (
              <Translation {...TXTWCL_n_a} />
            ) : (
              <CurrencyAmount value={totalPotentialReturns} />
            )}
          </span>
        </li>
      </ul>
    </div>
  );
};

export default Totals;
